/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1684713600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon May 22 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "kustomer",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Kustomer",
          "depth": 0,
          "id": "connect-to-kustomer"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing companies",
          "depth": 1,
          "id": "syncing-companies"
        }, {
          "value": "Syncing customers",
          "depth": 1,
          "id": "syncing-customers"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      img: "img",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Companies"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Kustomer companies"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.kustomer.com/kustomer-api-docs/reference/bulkcreatecompanies",
                children: "Companies docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customers"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Kustomer customers"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.kustomer.com/kustomer-api-docs/reference/bulkcreatecustomers",
                children: "Customers docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-kustomer",
        children: "Connect to Kustomer"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Kustomer"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Kustomer"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Region"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "API Key"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can generate an API key by navigating to ", _jsx(_components.strong, {
          children: "Settings > Security > API Keys > Add API Key"
        }), " in your Kustomer settings.\nFor more information, check out the ", _jsx(_components.a, {
          href: "https://help.kustomer.com/api-keys-SJs5YTIWX",
          children: "Kustomer's API key documentation"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-kustomer-apikey.png",
          alt: "Generating a Kustomer API Key"
        })
      }), "\n", _jsx(_components.p, {
        children: "You need to grant the following roles to your API key:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "org.permission.bulk"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "org.permission.company"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "org.permission.customer"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Kustomer destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Kustomer destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-companies",
        children: "Syncing companies"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Kustomer companies."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to companies in Kustomer, you need to select a model column and corresponding Kustomer field. You can match on any of the following Kustomer fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Company Name"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync company fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to default and custom company fields.\nKustomer requires the ", _jsx(_components.strong, {
          children: "Name"
        }), " field, so you must map it to complete your configuration.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["You must suffix custom field names with their data type.\nFor example, if you were syncing to a custom field called ", _jsx(_components.strong, {
          children: "Age"
        }), " that accepted numerical values, the Kustomer field name would be ", _jsx(_components.strong, {
          children: "AgeNum"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-kustomer-custom-mappings.png",
          alt: "Creating custom fields while field mapping"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, check out the ", _jsx(_components.a, {
          href: "https://developer.kustomer.com/kustomer-api-docs/reference/createacompany",
          children: "Kustomer documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "split-retries",
        children: "Split retries"
      }), "\n", _jsxs(_components.p, {
        children: ["Kustomer counts all companies in a batch as rejected if the request contains a single invalid company. To pinpoint which companys are getting rejected with which errors and reduce the number of valid companys that get retried, you can enable ", _jsx(_components.a, {
          href: "/syncs/retries#split-retries",
          children: "split retries"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-customers",
        children: "Syncing customers"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Kustomer customers."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to customers in Kustomer, you need to select a model column and corresponding Kustomer field. You can match on any of the following Kustomer fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Home Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Work Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Other Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Work Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Mobile Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Home Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Fax Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Other Phone"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync customer fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to default and custom customer fields.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["You must suffix custom field names with their data type. For example, if you were syncing to a custom field called ", _jsx(_components.strong, {
          children: "Age"
        }), " that accepted numerical values, the Kustomer field name would be ", _jsx(_components.strong, {
          children: "AgeNum"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-kustomer-custom-mappings.png",
          alt: "Creating custom fields while field mapping"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, check out the ", _jsx(_components.a, {
          href: "https://developer.kustomer.com/kustomer-api-docs/reference/createacustomer",
          children: "Kustomer documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the customer in Kustomer with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced customers from Kustomer"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "split-retries-1",
        children: "Split retries"
      }), "\n", _jsxs(_components.p, {
        children: ["Kustomer counts all customers in a batch as rejected if the request contains a single invalid customer. To pinpoint which customers are getting rejected with which errors and reduce the number of valid customers that get retried, you can enable ", _jsx(_components.a, {
          href: "/syncs/retries#split-retries",
          children: "split retries"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
